import { useCallback, useMemo } from 'react';
import { NarrativeMiniValuatorForm } from './NarrativeMiniValuatorForm';
import type { NarrativeForm } from '@/pages/Narrative/utils/schema';
import type { NarrativeMiniValuatorScreen } from '../utils';
import { NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM } from '../utils';
import type { NarrativeValuatorCompany } from '@/pages/Narrative/hooks/narrativeValuatorCompanyFragment';
import { useCreatePublicNarrative } from '@/pages/Narrative/hooks/useCreatePublicNarrative';
import { useTracking } from 'react-tracking';
import { transformFormValuesToValuation } from '@/pages/Narrative/utils/transformation';
import { usePathname } from '@/hooks/location/usePathname';
import { ROUTE_PUBLIC_NARRATIVE_CREATE } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { useNarrativeInvalidate } from '@/pages/Narrative/hooks/useNarrativeInvalidate';
import { usePastAnnualRevenueEarnings } from '@/pages/Narrative/hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';
import {
  CreateNarrativeFuturePEMultipleCalculator,
  FuturePEMultipleContext,
  useFuturePEMultipleCalculation,
} from '../../Calculators';
import { useNarrativeWatchCompanyContext } from '../../NarrativeWatchCompany';
import type { HeadingElements } from '@/utilities/heading';
type Props = {
  company: NarrativeValuatorCompany;
  setScreen: (screen: NarrativeMiniValuatorScreen) => void;
  hideCompanySelect?: boolean;
  onSliderInteraction?: (isInteracting: boolean) => void;
  titleAs: HeadingElements;
};

export const NarrativeMiniValuatorFormCreate = ({
  company,
  setScreen,
  hideCompanySelect,
  onSliderInteraction,
  titleAs,
}: Props) => {
  const { trackEvent } = useTracking();
  const { invalidate } = useNarrativeInvalidate();
  const { open } = useNarrativeWatchCompanyContext();

  const { mutate: createNarrative, isPending } = useCreatePublicNarrative({
    onSuccess: () => {
      invalidate();
      trackEvent({
        action: 'save',
      });
      open({ companyId: company.id });
    },
  });

  const handleSubmit = useCallback(
    (values: NarrativeForm) => {
      const { id } = company;
      const { title } = values;

      createNarrative({
        input: {
          companyId: id,
          contentBlocks: [],
          title,
          valuations: [
            transformFormValuesToValuation({
              values,
            }),
          ],
        },
        setPrimary: true,
      });
    },
    [createNarrative, company]
  );

  const {
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateFairValue,
  } = useFuturePEMultipleCalculation(
    new FuturePEMultipleContext(
      new CreateNarrativeFuturePEMultipleCalculator(company)
    )
  );

  const initialNarrativeForm: NarrativeForm = useMemo(
    () => ({
      ...calculateFairValueFromInitialisation(),
      userHoldsPosition: null,
      title: '',
      content: {
        text: '',
        html: '',
      },
    }),
    [calculateFairValueFromInitialisation]
  );

  const pathname = usePathname();

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
  });

  return (
    <NarrativeMiniValuatorForm
      company={company}
      pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
      initialValues={initialNarrativeForm}
      setScreen={setScreen}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      saveLabel="Set Fair Value"
      enableReinitialize={false}
      fullScreenPath={getPath({
        type: ROUTE_PUBLIC_NARRATIVE_CREATE,
        payload: {
          query: {
            company: company.id,
            returnPath: `${pathname}?${NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM}=${company.canonicalURL}`,
          },
        },
      })}
      calculateFairValue={calculateFairValue}
      calculateRevenueGrowthRateFromFairValue={
        calculateRevenueGrowthRateFromFairValue
      }
      hideCompanySelect={hideCompanySelect}
      onSliderInteraction={onSliderInteraction}
      titleAs={titleAs}
    />
  );
};
